import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ConfirmationDialog} from '@primer/react'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {MESSAGES} from '../../constants/messages'
import {commitDeleteIssueMutation, useDeleteIssueMutation} from '../../mutations/delete-issue-mutation'
import styles from './IssueDeletionConfirmationDialog.module.css'

type IssueDeletionConfirmationDialogProps = {
  issueId: string
  owner: string
  repository: string
  number: number
  onSuccessfulDeletion?: (issueId: string) => void
  afterDelete?: () => void
  onClose: () => void
}

export const IssueDeletionConfirmationDialog = ({
  issueId,
  owner,
  repository,
  number,
  onSuccessfulDeletion,
  afterDelete,
  onClose,
}: IssueDeletionConfirmationDialogProps) => {
  const environment = useRelayEnvironment()
  const useTanStack = isFeatureEnabled('issue_viewer_lifecycle_mutations_tanstack')
  const onCompleted = useCallback(() => {
    onSuccessfulDeletion?.(issueId)
    afterDelete?.()
  }, [afterDelete, issueId, onSuccessfulDeletion])
  const deleteIssueMutation = useDeleteIssueMutation({
    owner,
    repository,
    number,
    onCompleted,
  })

  const onDialogClose = useCallback(
    (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
      if (gesture === 'confirm') {
        if (useTanStack) {
          deleteIssueMutation.mutate()
        } else {
          commitDeleteIssueMutation({
            environment,
            input: {issueId},
            onCompleted,
          })
        }
      }
      onClose()
    },
    [deleteIssueMutation, environment, issueId, onClose, onCompleted, useTanStack],
  )

  return (
    <ConfirmationDialog
      title={MESSAGES.deleteIssueConfirmationTitle}
      confirmButtonContent={MESSAGES.deleteIssueConfirmationButton}
      confirmButtonType="danger"
      onClose={onDialogClose}
    >
      <div className={styles.confirmationContent}>
        <ul className={styles.confirmationList}>
          {MESSAGES.deleteIssueConfirmationActionPoints.map(actionPoint => (
            <li key={actionPoint}>
              <span>{actionPoint}</span>
            </li>
          ))}
        </ul>
      </div>
    </ConfirmationDialog>
  )
}
